import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocalizationStore } from '@stores/localizationStore';
import { getAvailableLanguages } from '@services/localizationService';
import Select, { type SelectOption } from '@workpay/ds-shared/components/ui/Select';
import { Flag } from '@workpay/ds-shared/components/ui/Flag';
import styles from './LocaleSwitcher.module.scss';

/** Map locale codes to display names using browser-native Intl API.
 *  Mirrors the helper used by tenant-onboarding's TopNav so both selectors
 *  show identical labels ("English", "العربية", "Français", ...). */
function getLanguageDisplayName(locale: string): string {
  try {
    const displayNames = new Intl.DisplayNames([locale], { type: 'language' });
    const name = displayNames.of(locale);
    return name ? name.charAt(0).toUpperCase() + name.slice(1) : locale;
  } catch {
    return locale;
  }
}

export function LocaleSwitcher() {
  const language = useLocalizationStore((s) => s.language);
  const setLanguage = useLocalizationStore((s) => s.setLanguage);

  const { data: languages } = useQuery({
    queryKey: ['localization', 'languages'],
    queryFn: getAvailableLanguages,
    staleTime: Infinity,
  });

  // Defence in depth — the service normalises the API into `string[]`, but
  // an unexpected envelope shape in some deploy must not crash the tree.
  const safeLanguages = Array.isArray(languages) ? languages : null;

  const options: SelectOption[] = useMemo(
    () =>
      (safeLanguages ?? [language]).map((lang) => ({
        label: getLanguageDisplayName(lang),
        value: lang,
        icon: <Flag locale={lang} title={getLanguageDisplayName(lang)} />,
      })),
    [safeLanguages, language],
  );

  if (!safeLanguages || safeLanguages.length <= 1) return null;

  return (
    <div className={styles.wrapper}>
      <div className={styles.selectShell}>
        <Select options={options} value={language} onChange={setLanguage} size="sm" />
      </div>
    </div>
  );
}
